import { useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Crown, Download, ExternalLink, Eye, IdCard, MessageCircle, Pencil, Share2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Btn, Empty, Page, Spinner, TopBar } from "@/components/kit";
import { Switch } from "@/components/ui/switch";
import DbcPreview from "@/components/DbcPreview";
import { usePaymentConfig } from "@/hooks/queries";
import { abs, cardBody, copyText, DEFAULT_THEME, useDbc, useDbcExtra, useSaveDbc } from "@/lib/dbc";
import { periodLabel, rupees } from "@/lib/toto";

const SAMPLE = { fullName: "Your Name", designation: "Founder", company: "Your Company", phone: "+91 98765 43210", email: "you@company.com", website: "yourcompany.com", socials: { linkedin: "x", instagram: "x" } };

function Upsell() {
  const pay = usePaymentConfig().data;
  return (
    <div className="grid gap-8 lg:grid-cols-2 items-start mt-2" data-testid="dbc-upsell">
      <div className="relative">
        <div className="pointer-events-none select-none opacity-70 blur-[1px]"><DbcPreview card={SAMPLE} testid="dbc-sample" /></div>
        <span className="absolute top-4 right-4 inline-flex items-center gap-1 rounded-full bg-[#7C3AED] text-white text-[12px] font-bold px-3 py-1"><Crown className="w-3.5 h-3.5" />Premium</span>
      </div>
      <div>
        <h2 className="text-[24px] font-extrabold">Your own Digital Business Card</h2>
        <ul className="mt-4 space-y-2 text-[15px] text-[#334155] list-disc pl-5">
          <li>A shareable page with your photo, contact details and social links</li>
          <li>A QR code people can scan to save you instantly</li>
          <li>Share on WhatsApp or copy your link anywhere</li>
        </ul>
        <Link to="/subscription" className="t-btn t-btn-primary mt-6 w-full sm:w-auto px-8" data-testid="dbc-upgrade">{pay ? `Upgrade to Premium · ${rupees(pay.amount)}/${periodLabel(pay.durationDays)}` : "Upgrade to Premium"}</Link>
      </div>
    </div>
  );
}

function ShareActions({ card }) {
  const url = card.publicUrl;
  const copy = async () => toast[(await copyText(url)) ? "success" : "error"]((await copyText(url)) ? "Link copied." : "Couldn't copy the link.");
  const share = async () => {
    if (navigator.share) try { return await navigator.share({ title: card.fullName, text: `${card.fullName} — Digital Business Card`, url }); } catch (e) { if (e?.name === "AbortError") return; }
    copy();
  };
  const acts = [[Copy, "Copy Link", copy, "dbc-copy"], [MessageCircle, "WhatsApp", () => window.open(`https://wa.me/?text=${encodeURIComponent(url)}`, "_blank", "noopener"), "dbc-whatsapp"], [Share2, "Share", share, "dbc-share"], [ExternalLink, "Open Page", () => window.open(url, "_blank", "noopener"), "dbc-open"]];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="dbc-share-actions">
      {acts.map(([I, l, fn, id]) => <button key={id} onClick={fn} className="t-card flex flex-col items-center gap-2 py-4 text-[13px] font-semibold hover:border-[#0000EC] transition-colors" data-testid={id}><I className="w-5 h-5 text-[#0000EC]" />{l}</button>)}
    </div>
  );
}

function QrBlock({ card }) {
  const qr = abs(card.qrUrl);
  const blob = async () => (await fetch(qr)).blob();
  const download = async () => {
    try {
      const u = URL.createObjectURL(await blob());
      Object.assign(document.createElement("a"), { href: u, download: `${card.slug || "toto-card"}-qr.png` }).click();
      setTimeout(() => URL.revokeObjectURL(u), 1000);
    } catch { window.open(qr, "_blank", "noopener"); }
  };
  const share = async () => {
    try {
      const f = new File([await blob()], "toto-card-qr.png", { type: "image/png" });
      if (navigator.canShare?.({ files: [f] })) return await navigator.share({ files: [f], title: card.fullName, text: card.publicUrl });
    } catch (e) { if (e?.name === "AbortError") return; }
    toast[(await copyText(card.publicUrl)) ? "success" : "error"]("Sharing images isn't supported here — link copied instead.");
  };
  return (
    <div className="t-card p-5 flex flex-col sm:flex-row items-center gap-5" data-testid="dbc-qr">
      <img src={qr} alt="QR code" className="w-40 h-40 rounded-xl border border-[#E2E8F0] bg-white" data-testid="dbc-qr-image" />
      <div className="flex-1 w-full">
        <p className="font-bold text-[16px]">Your QR code</p>
        <p className="text-[13px] text-[#64748B] mt-1">Anyone can scan it to open your card and save your contact.</p>
        <div className="flex gap-2 mt-4">
          <Btn variant="outline" className="h-11 text-[14px] flex-1 gap-2" onClick={download} testid="dbc-qr-download"><Download className="w-4 h-4" />Download</Btn>
          <Btn variant="outline" className="h-11 text-[14px] flex-1 gap-2" onClick={share} testid="dbc-qr-share"><Share2 className="w-4 h-4" />Share</Btn>
        </div>
      </div>
    </div>
  );
}

function Stats({ stats }) {
  const items = [[Eye, "Card views", "views"], [UserPlus, "Contact saves", "saves"]];
  return (
    <div className="t-card p-5" data-testid="dbc-stats">
      <p className="font-bold text-[16px]">Last 7 days</p>
      <div className="grid grid-cols-2 gap-3 mt-3">
        {items.map(([I, l, k]) => (
          <div key={k} className="rounded-[14px] bg-[#F8FAFC] p-4">
            <I className="w-5 h-5 text-[#0000EC]" />
            <p className="text-[28px] font-extrabold mt-1 leading-none" data-testid={`dbc-stat-week-${k}`}>{stats?.week?.[k] ?? 0}</p>
            <p className="text-[13px] text-[#64748B] mt-1">{l}</p>
            <p className="text-[12px] text-[#94A3B8] mt-0.5" data-testid={`dbc-stat-total-${k}`}>{stats?.total?.[k] ?? 0} all-time</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Dashboard({ card }) {
  const extra = useDbcExtra().data;
  const save = useSaveDbc();
  const [busy, setBusy] = useState(false);
  const toggle = async (enabled) => {
    setBusy(true);
    try { await save({ ...cardBody(card), enabled }); toast.success(enabled ? "Card is live." : "Card turned off."); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr] items-start mt-2">
      <div className="space-y-3">
        <DbcPreview card={card} color={extra?.theme || DEFAULT_THEME} pattern={extra?.pattern} />
        <Link to="/dbc/edit" className="t-btn t-btn-outline w-full gap-2" data-testid="dbc-edit"><Pencil className="w-4 h-4" />Edit Card</Link>
      </div>
      <div className="space-y-5">
        <div className="t-card p-5 flex items-center gap-4" data-testid="dbc-status">
          <div className="flex-1">
            <p className="font-bold text-[16px]">{card.enabled ? "Your card is live" : "Your card is off"}</p>
            <p className="text-[13px] text-[#64748B] mt-0.5 break-all" data-testid="dbc-public-url">{card.enabled ? card.publicUrl : "Turn it on to share your link and QR code."}</p>
          </div>
          <Switch checked={!!card.enabled} disabled={busy} onCheckedChange={toggle} data-testid="dbc-enabled-toggle" />
        </div>
        {card.enabled && <><ShareActions card={card} /><QrBlock card={card} /></>}
        <Stats stats={extra?.stats} />
      </div>
    </div>
  );
}

export default function DigitalCard() {
  const q = useDbc();
  const d = q.data;
  let body;
  if (q.isLoading) body = <Spinner />;
  else if (q.error) body = <Empty icon={IdCard} title={q.error.status === 404 ? "Digital cards are coming soon" : "Couldn't load your card"} text={q.error.status === 404 ? "This feature isn't available on the server yet." : q.error.message} testid="dbc-error" />;
  else if (!d.isPremium) body = <Upsell />;
  else if (!d.exists) body = <Empty icon={IdCard} title="Create your Digital Business Card" text="Share your details with a link or QR code — no paper needed." action={<Link to="/dbc/edit" className="t-btn t-btn-primary px-8" data-testid="dbc-create">Create My Card</Link>} testid="dbc-empty" />;
  else body = <Dashboard card={d.card} />;
  return <Page><TopBar title="Digital Business Card" backTo="/profile" align="left" />{body}</Page>;
}
